import React, { useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Modal,
  Platform,
} from 'react-native';
import DateTimePicker from '@react-native-community/datetimepicker';
import { MARKETPLACE_COLORS } from '@/constants/residents/marketplaceColors';
import { StepHeader, ToggleBar, maintenanceStepStyles } from './shared';
import { PermissionChoice } from '@/hooks/residents/useMaintenanceForm';
import { SVG_COMPONENTS } from '@/assets/admin';

interface StepEntryPermissionProps {
  permissionChoice: PermissionChoice;
  onPermissionChoiceChange: (value: PermissionChoice) => void;
  timeSlots: string[];
  onTimeSlotsChange: (slots: string[]) => void;
}

/**
 * StepEntryPermission - Step 5: Entry Permission
 * Allows user to grant entry permission or schedule time slots
 */
export const StepEntryPermission: React.FC<StepEntryPermissionProps> = ({
  permissionChoice,
  onPermissionChoiceChange,
  timeSlots,
  onTimeSlotsChange,
}) => {
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [currentSlotIndex, setCurrentSlotIndex] = useState<number | null>(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  
  const isYesSelected = permissionChoice === 'yes';
  const toggleIndex = isYesSelected ? 0 : 1;

  const handleToggleChange = (index: number) => {
    onPermissionChoiceChange(index === 0 ? 'yes' : 'no');
    if (index === 0) {
      onTimeSlotsChange([]); // Clear time slots when switching to "Yes"
    }
  };

  const handleTimeSlotPress = (index: number) => {
    setCurrentSlotIndex(index);
    setSelectedDate(timeSlots[index] ? new Date(timeSlots[index]) : new Date());
    setShowDatePicker(true);
  };

  const handleDateChange = (event: any, date?: Date) => {
    if (Platform.OS === 'android') {
      setShowDatePicker(false);
    }
    if (date && currentSlotIndex !== null) {
      const newSlots = [...timeSlots];
      newSlots[currentSlotIndex] = date.toISOString();
      onTimeSlotsChange(newSlots);
    }
  };

  const handleDatePickerDone = () => {
    setShowDatePicker(false);
    if (currentSlotIndex !== null) {
      const newSlots = [...timeSlots];
      newSlots[currentSlotIndex] = selectedDate.toISOString();
      onTimeSlotsChange(newSlots);
    }
  };

  const formatTimeSlot = (dateString?: string): string => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    } catch {
      return '';
    }
  };

  // Initialize 3 empty slots if switching to "No"
  React.useEffect(() => {
    if (permissionChoice === 'no' && timeSlots.length < 3) {
      onTimeSlotsChange(['', '', '']);
    }
  }, [permissionChoice]);

  return (
    <View style={maintenanceStepStyles.container}>
      <StepHeader title="Can our technician enter your residence?" />
      
      <View style={maintenanceStepStyles.content}>
        {/* Yes/No Toggle Bar */}
        <ToggleBar
          options={['Yes', 'No']}
          selectedIndex={toggleIndex}
          onSelect={handleToggleChange}
        />

        {/* Time Slots Section - Only show when "No" is selected */}
        {permissionChoice === 'no' && (
          <View style={styles.timeSlotsSection}>
            {/* Alert Banner */}
            <View style={styles.alertBox}>
              <AlertIcon />
              <Text style={styles.alertText}>
                Please select 3 time slots in the next 10 days
              </Text>
            </View>

            {/* Time Slot Fields */}
            {[0, 1, 2].map((index) => (
              <TouchableOpacity
                key={index}
                style={styles.dateTimeField}
                onPress={() => handleTimeSlotPress(index)}
                activeOpacity={0.7}
              >
                <View style={styles.dateTimeContent}>
                  <View style={styles.dateTimeTextContainer}>
                    {timeSlots[index] ? (
                      <>
                        <Text style={styles.dateTimeLabel}>Date and time</Text>
                        <Text style={styles.dateTimeValue}>
                          {formatTimeSlot(timeSlots[index])}
                        </Text>
                      </>
                    ) : (
                      <Text style={styles.dateTimePlaceholder}>Date and time</Text>
                    )}
                  </View>
                  <CalendarIcon />
                </View>
              </TouchableOpacity>
            ))}
          </View>
        )}
      </View>

      {/* Date Time Picker Modal for iOS */}
      {Platform.OS === 'ios' && showDatePicker && (
        <Modal
          visible={showDatePicker}
          transparent={true}
          animationType="slide"
          onRequestClose={() => setShowDatePicker(false)}
        >
          <View style={styles.pickerModalOverlay}>
            <View style={styles.pickerModalContent}>
              <View style={styles.pickerModalHeader}>
                <TouchableOpacity onPress={() => setShowDatePicker(false)}>
                  <Text style={styles.pickerModalCancel}>Cancel</Text>
                </TouchableOpacity>
                <Text style={styles.pickerModalTitle}>Select Date & Time</Text>
                <TouchableOpacity onPress={handleDatePickerDone}>
                  <Text style={styles.pickerModalDone}>Done</Text>
                </TouchableOpacity>
              </View>
              <DateTimePicker
                value={selectedDate}
                mode="datetime"
                display="spinner"
                onChange={(event, date) => {
                  if (date) setSelectedDate(date);
                }}
                minimumDate={new Date()}
                maximumDate={new Date(Date.now() + 10 * 24 * 60 * 60 * 1000)}
                textColor={MARKETPLACE_COLORS.textPrimary}
              />
            </View>
          </View>
        </Modal>
      )}

      {/* Android DateTimePicker */}
      {Platform.OS === 'android' && showDatePicker && (
        <DateTimePicker
          value={selectedDate}
          mode="datetime"
          display="default"
          onChange={handleDateChange}
          minimumDate={new Date()}
          maximumDate={new Date(Date.now() + 10 * 24 * 60 * 60 * 1000)}
        />
      )}
    </View>
  );
};

// Alert Icon Component
const AlertIcon: React.FC = () => (
  <View style={styles.alertIcon}>
    <View style={styles.alertIconCircle}>
      <Text style={styles.alertIconText}>!</Text>
    </View>
  </View>
);

// Calendar Icon Component
const CalendarIcon: React.FC = () => (
  <View style={styles.calendarIcon}>
    <View style={styles.calendarBody}>
      <View style={styles.calendarTop} />
      <View style={styles.calendarGrid}>
        <View style={styles.calendarDot} />
        <View style={styles.calendarDot} />
        <View style={styles.calendarDot} />
        <View style={styles.calendarDot} />
      </View>
    </View>
  </View>
);

const styles = StyleSheet.create({
  timeSlotsSection: {
    gap: 20,
    marginTop: 24,
  },
  
  // Alert
  alertBox: {
    backgroundColor: 'rgba(255, 118, 41, 0.1)',
    borderRadius: 8,
    padding: 16,
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
  },
  alertIcon: {
    width: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  alertIconCircle: {
    width: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: '#FF7629',
    alignItems: 'center',
    justifyContent: 'center',
  },
  alertIconText: {
    fontSize: 10,
    fontWeight: '700',
    color: '#FFFFFF',
  },
  alertText: {
    flex: 1,
    fontSize: 14,
    fontWeight: '400',
    color: '#FF7629',
    lineHeight: 21,
  },
  
  // Date/Time Field
  dateTimeField: {
    backgroundColor: MARKETPLACE_COLORS.uploadAreaBackground,
    borderRadius: 8,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    borderBottomWidth: 1,
    borderBottomColor: MARKETPLACE_COLORS.textPrimary,
  },
  dateTimeContent: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 12,
  },
  dateTimeTextContainer: {
    flex: 1,
    gap: 2,
  },
  dateTimeLabel: {
    fontSize: 12,
    fontWeight: '400',
    color: MARKETPLACE_COLORS.textSecondary,
    lineHeight: 12,
    letterSpacing: 0.15,
  },
  dateTimeValue: {
    fontSize: 16,
    fontWeight: '400',
    color: MARKETPLACE_COLORS.textPrimary,
    lineHeight: 24,
  },
  dateTimePlaceholder: {
    fontSize: 16,
    fontWeight: '400',
    color: MARKETPLACE_COLORS.textSecondary,
    lineHeight: 24,
  },
  
  // Calendar Icon
  calendarIcon: {
    width: 24,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  calendarBody: {
    width: 18,
    height: 18,
    borderWidth: 1.5,
    borderColor: MARKETPLACE_COLORS.textPrimary,
    borderRadius: 3,
    padding: 2,
  },
  calendarTop: {
    width: '100%',
    height: 3,
    backgroundColor: MARKETPLACE_COLORS.textPrimary,
    marginBottom: 2,
  },
  calendarGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 2,
  },
  calendarDot: {
    width: 3,
    height: 3,
    backgroundColor: MARKETPLACE_COLORS.textPrimary,
    borderRadius: 1,
  },
  
  // Picker Modal (iOS)
  pickerModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  pickerModalContent: {
    backgroundColor: MARKETPLACE_COLORS.cardBackground,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingBottom: 20,
  },
  pickerModalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: MARKETPLACE_COLORS.dividerGray,
  },
  pickerModalTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: MARKETPLACE_COLORS.textPrimary,
  },
  pickerModalCancel: {
    fontSize: 16,
    color: MARKETPLACE_COLORS.textSecondary,
  },
  pickerModalDone: {
    fontSize: 16,
    fontWeight: '600',
    color: MARKETPLACE_COLORS.accentBlue,
  },
});

export default StepEntryPermission;
